import React from "react";
import { Building2, EllipsisVertical, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Project } from "@/types";
import { cn } from "@/lib/utils";
import { SUPPORTED_CURRENCIES } from "@/api/currency";

interface ProjectCardProps {
  project: Project;
  showOfficialOnly?: boolean;
  selectedCurrency?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  showOfficialOnly = false,
  selectedCurrency = "TRY",
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statusKey =
    project.status === "delayed" ? "stopped" : project.status === "completed" ? "completed" : "ongoing";

  const statusLabel =
    statusKey === "stopped"
      ? t("projects.status.delayed")
      : statusKey === "completed"
        ? t("projects.status.completed")
        : t("projects.status.active");

  const isStopped = statusKey === "stopped";

  const units = project.details?.units || [];
  const flatsCountFromUnits = units.filter((unit) => unit.type === "flat").length;
  const shopsCountFromUnits = units.filter((unit) => unit.type === "shop").length;

  const flatsCount = flatsCountFromUnits > 0 ? flatsCountFromUnits : (project.details?.flats || 0);
  const shopsCount = shopsCountFromUnits > 0 ? shopsCountFromUnits : (project.details?.shops || 0);
  const hasUnitInfo = flatsCount + shopsCount > 0;

  const projectType = flatsCount > 0 && shopsCount > 0
    ? "Daire + Dükkan"
    : flatsCount > 0
      ? "Daire"
      : shopsCount > 0
        ? "Dükkan"
        : null;
  const totalUnitArea =
    project.details?.units?.reduce((sum, unit) => sum + (Number(unit.area) || 0), 0) ?? 0;
  const area = totalUnitArea > 0 ? totalUnitArea : (project.details?.area ?? 0);

  const normalizedCurrency = selectedCurrency === "KWD" ? "DINAR" : selectedCurrency;
  const fallbackCurrency = normalizedCurrency === "DINAR" ? "KWD" : normalizedCurrency;
  const detailed = project.detailedCosts as any;

  const detailedAmount =
    detailed?.[showOfficialOnly ? "official" : "total"]?.[selectedCurrency] ??
    detailed?.[showOfficialOnly ? "official" : "total"]?.[normalizedCurrency] ??
    detailed?.[showOfficialOnly ? "official" : "total"]?.[fallbackCurrency] ??
    detailed?.[selectedCurrency]?.[showOfficialOnly ? "official" : "total"] ??
    detailed?.[normalizedCurrency]?.[showOfficialOnly ? "official" : "total"] ??
    detailed?.[fallbackCurrency]?.[showOfficialOnly ? "official" : "total"];

  const fallbackAmount =
    !showOfficialOnly
      ? selectedCurrency === "TRY"
        ? project.costs.try ?? 0
        : selectedCurrency === "USD"
          ? project.costs.usd ?? 0
          : selectedCurrency === "EUR"
            ? project.costs.eur ?? 0
            : 0
      : 0;

  const estimatedCost = typeof detailedAmount === "number" ? detailedAmount : fallbackAmount;

  const currencySymbol =
    selectedCurrency === "KWD"
      ? "KWD"
      : SUPPORTED_CURRENCIES.find((currency) => currency.value === normalizedCurrency)?.symbol || selectedCurrency;
  const progress = Math.min(Math.max(project.progress || 0, 0), 100);
  const formattedArea = Math.round(area).toLocaleString("tr-TR");
  const formattedCost = Math.round(estimatedCost).toLocaleString("tr-TR");

  return (
    <button
      type="button"
      className={cn(
        "w-full overflow-hidden rounded-[10px] border bg-white text-left shadow-[0_2px_2px_rgba(10,13,18,0.06),0_4px_4px_rgba(10,13,18,0.1)] transition-shadow hover:shadow-md",
        "border-[var(--brand-50,#EBF4FF)]",
      )}
      onClick={() => navigate(`/project/${project.id}`)}
    >
      <div
        className={cn(
          "flex h-[50px] items-center justify-between px-[14px] py-[10px]",
          isStopped ? "bg-[var(--purple-25,#FAFAFF)]" : "bg-[var(--warning-25,#FFFCF5)]",
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn(
              "flex h-[22px] w-[22px] items-center justify-center rounded-[4px] border",
              isStopped
                ? "border-[var(--purple-500,#7A5AF8)] bg-[var(--purple-600,#6938EF)]"
                : "border-[var(--warning-300,#FEC84B)] bg-[var(--warning-400,#FDB022)]",
            )}
          >
            {isStopped ? (
              <ShoppingBag className="h-3 w-3 text-[var(--brand-50,#EBF4FF)]" />
            ) : (
              <Building2 className="h-3 w-3 text-[var(--warning-25,#FFFCF5)]" />
            )}
          </div>
          <p className="truncate text-[14px] font-medium text-[var(--gray-900,#181D27)]">{project.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-[16px] border border-[#D2E6FF] bg-[var(--brand-50,#EBF4FF)] px-[10px] py-[2px] text-[12px] font-bold uppercase tracking-[0.6px] text-[var(--brand-600,#1565C0)]">
            {statusLabel}
          </span>
          <EllipsisVertical className="h-[18px] w-[18px] text-[var(--brand-400,#4494DC)]" />
        </div>
      </div>

      <div className="flex items-center justify-between bg-white px-[14px] py-[10px]">
        <p className="text-[12px] font-bold uppercase tracking-[0.6px] text-[var(--brand-800,#0C3F7A)]">
          {t("projects.area", { defaultValue: "Alan" })}
        </p>
        <div className="flex items-center gap-1">
          <span className="font-mono text-[16px] leading-6 text-[var(--gray-700,#414651)]">{formattedArea}</span>
          <span className="text-[14px] font-medium text-[var(--gray-400,#A4A7AE)]">m²</span>
        </div>
      </div>

      <div className="flex items-center justify-between bg-white px-[14px] py-[10px]">
        <p className="text-[12px] font-bold uppercase tracking-[0.6px] text-[var(--brand-800,#0C3F7A)]">
          <span>{t("projects.cost", { defaultValue: "Maliyet" })} </span>
          <span className="text-[#8D9FBC]">({t("projects.estimated", { defaultValue: "Tahmini" })})</span>
        </p>
        <div className="flex items-center gap-1">
          <span className="font-mono text-[14px] leading-6 text-[var(--gray-900,#181D27)]">{formattedCost}</span>
          <span className="text-[12px] text-[var(--gray-600,#535862)]">{currencySymbol}</span>
        </div>
      </div>

      {hasUnitInfo && projectType && (
        <div className="flex items-center justify-between bg-white px-[14px] py-[10px]">
          <p className="text-[12px] font-bold uppercase tracking-[0.6px] text-[var(--brand-800,#0C3F7A)]">
            {t("projects.type", { defaultValue: "Tür" })}
          </p>
          <span className="text-[14px] text-[var(--gray-900,#181D27)]">{projectType}</span>
        </div>
      )}

      <div className="bg-white px-[10px] pb-[10px] pt-[10px]">
        <div className="flex items-center gap-3">
          <div className="relative h-2 flex-1 rounded-[4px] bg-[var(--gray-200,#E9EAEB)]">
            <div
              className="absolute left-0 top-0 h-full rounded-[4px] bg-[var(--brand-600,#1565C0)]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-[14px] font-medium text-[var(--gray-700,#414651)]">{progress}%</span>
        </div>
      </div>
    </button>
  );
};

export default ProjectCard;
